import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { AdultTpnInteruptAliases, FragmentNames } from '../../../../../codegen';
import { FULL_LOCALE_DATE, ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../../constants/routes';
import BaseController from '../../../../BaseController';
import { SolutionType } from '../../../adjust-infusion-speed/adjust-infusion-speed-types';
import { AdminModalsService } from '../../../services/admin-modals-service';
import MedicationService from '../../../services/medication-service';
import { PrescriptionType } from '../../../services/timeline-service';
import { EventType } from '../../enteral-nutrition/enteral-nutrition-type';
import { AdultTpnType, ReasonType } from '../adult-tpn-types';

@Controller({ component: AdultTpnInteruptAliases.ADULT_TPN_INTERRUPT, fragment: FragmentNames.ADULT_TPN_INTERUPT })
export default class AdultTpnInterruptController extends BaseController {

  @Inject(AdultTpnInteruptAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaAPI!: TextAreaAPI;

  @Inject(AdultTpnInteruptAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @Inject(AdultTpnInteruptAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(AdultTpnInteruptAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @Inject(AdultTpnInteruptAliases.TITLE_FIELD)
  private titleFieldAPI!: FieldAPI

  @Inject(AdultTpnInteruptAliases.INTERRUPT_FORM)
  private interruptFormAPI!: FormAPI

  @Inject(AdultTpnInteruptAliases.REASON_AUTOCOMPLETE)
  private reasonAutocompleteAPI!: AutocompleteAPI;

  @Inject(AdultTpnInteruptAliases.DATE_INPUT)
  private newDateInputApi!: InputAPI;

  @OnMount(AdultTpnInteruptAliases.ADULT_TPN_INTERRUPT)
  handleLayoutInterruptMount(): void {
    const eventType = EventType.INTERRUPTION_OF_SOLUTIONS;
    const discardedVolume = 0;
    const { adultTpn } = this.context.get('adultTpn') as { adultTpn: AdultTpnType }
    this.context.set({ adultTpn, eventType });
    this.titleFieldAPI.setData({ materialDescription: adultTpn.materialDescription });
    this.interruptFormAPI.setData({
      ...adultTpn,
      discardedVolume,
      infusedVolume: adultTpn.totalVolume,
      stageVolume: adultTpn.totalVolume
    });
  }

  @OnMount(AdultTpnInteruptAliases.SCHEDULE_DATE_TEXT)
  handleScheduleDaeFieldMount(event: MountEvent): void {
    const { adultTpn } = this.context.get('adultTpn') as { adultTpn: AdultTpnType }
    const inputApi = event.target as InputAPI;
    inputApi.setValue(moment(adultTpn.scheduleDate).format(FULL_LOCALE_DATE));
  }

  @OnBeforeCancel(AdultTpnInteruptAliases.INTERRUPT_FORM)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnMount(AdultTpnInteruptAliases.REASON_FETCH)
  handleFetchReasonMount(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;
    fetchReason.setParameters({ establishmentCode, status: PrescriptionType.ADULT_PARENTERAL_NUTRITION }, { skip: true });
  }

  @OnMount(AdultTpnInteruptAliases.DATE_INPUT)
  handleDateFieldMount(event: MountEvent): void {
    const inputApi = event.target as InputAPI;
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT) });
    inputApi.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnChange(AdultTpnInteruptAliases.DISCARDED_VOLUME_INPUT)
  handleDiscardedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  @OnChange(AdultTpnInteruptAliases.INFUSED_VOLUME_INPUT)
  handleInfusedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  @OnBeforeSubmit(AdultTpnInteruptAliases.INTERRUPT_FORM)
  handleBeforeSubmit(): void {
    this.interruptFormAPI.validate();
    const body = this.getRequestBody();
    this.generatePresciptionChangesInterrupt(body)
  }

  private getRequestBody(): Record<string, Value> {
    const adultTpn = this.context.get('adultTpn')
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const reasonSelected = this.reasonAutocompleteAPI.getValue() as unknown as ReasonType;
    const changeTypeInterrupt = 2;
    const changeDate = moment(this.newDateInputApi.getValue()).format(ISO_FULL_DATE_FORMAT)

    return {
      changeDate,
      changeType: changeTypeInterrupt,
      dateTime: null,
      despisedVolumeQuantity: this.discardedVolumeInputAPI.getValue(),
      dosageType: null,
      encounterNumber: encounterId as number,
      infusedVolumeQuantity: this.infusedVolumeInputAPI.getValue(),
      justificationDescription: this.notesJustificationTextAreaAPI.getValue() as string ?? reasonSelected.reasonDescription,
      partialVolumeQuantity: this.partialVolumeInputAPI.getValue(),
      prescriptionNumber: adultTpn.prescriptionNumber,
      reasonSequenceNumber: reasonSelected.sequenceNumber,
      solutionSequenceNumber: adultTpn.materialSequence,
      solutionType: SolutionType.ADULT_PARENTERAL_NUTRITION,
      volumePhaseQuantity: this.infusedVolumeInputAPI.getValue() as number,
    }
  }

  private generatePresciptionChangesInterrupt(body: Record<string, Value>): void {
    HttpService.post(Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR, {}, { body })
      .then(response => {
        const responseBody = response.body as { messageDescription: string };
        if (responseBody) {
          this.showResponseMessage(responseBody?.messageDescription);
          this.redirectToTimeline();
        }
      }).catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
        }
      })
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('tws.emar.interrupt-solution-success'), ToastLevel.SUCCESS);
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.ADULT_TPN_INTERUPT);
    AdminModalsService.redirectToTimeline(FragmentNames.ADULT_TPN_VIEWER);
  }

  private isValidChar(value: string, oldValue?: string): boolean {
    return value === '.' || value === ',' || (value === '' && oldValue !== '')
  }

  private handleVolumeInputChange({ value, oldValue }: ChangeEvent<string>, input: InputAPI): void {
    const actualValue = Number(value) || 0;
    if(this.isValidChar(value, oldValue) || isNaN(actualValue) || oldValue === value) {
      return
    }

    const adultTpn = this.context.get('adultTpn')
    const partialVolume = Number(this.partialVolumeInputAPI.getValue());
    const updatedVolume = this.calculateVolumeSolution(actualValue, partialVolume, Number(adultTpn?.totalVolume));
    input.setValue(String(updatedVolume));
  }

  private calculateVolumeSolution(infusedVolume: number, partialVolume: number, stageVolume: number): number {
    const defaultValue = 0;
    if (stageVolume > defaultValue) {
      const result = stageVolume - partialVolume - infusedVolume;
      return Math.max(defaultValue, parseFloat(result.toFixed(2)));
    }

    return defaultValue;
  }
}